"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useElectricalWithHVAC = exports.HVAC_BRANCH_INDEX_BASE = void 0;
const react_1 = require("react");
const useBranchEnergyCalculation_1 = require("./useBranchEnergyCalculation");
const queries_1 = require("../../graphql/queries");
const amplifyBootstrap_1 = require("../../utils/amplifyBootstrap");
const pollingConstants_1 = require("../../utils/pollingConstants");
const useHVACSensorsByGroup_1 = require("../hvac/useHVACSensorsByGroup");
exports.HVAC_BRANCH_INDEX_BASE = 100;
const SPLIT_PHASE_VOLTAGE_FALLBACK = 240;
const SINGLE_LEG_THRESHOLD = 180;
const fetchGroupSeerRating = async (hubId, groupId) => {
    try {
        const variables = { hubId };
        if (groupId)
            variables.groupId = groupId;
        const response = await amplifyBootstrap_1.API.graphql({ query: queries_1.getSeerRating, variables });
        return response.data?.getSeerRating ?? null;
    }
    catch (err) {
        console.error(`Error fetching SEER rating for group ${groupId}:`, err);
        return null;
    }
};
const getGroupLabel = (group, index, totalGroups) => {
    // On single-group hubs we omit the group label so branch names stay short ("Condenser",
    // "Air Handler"). On multi-group hubs we prefix with the sensor location so the user can
    // tell units apart ("Upstairs Condenser" etc.).
    if (totalGroups === 1)
        return '';
    return group.indoorSensor?.location || group.outdoorSensor?.location || `System ${index + 1}`;
};
const buildHvacEntryName = (label, kind) => label ? `${label} ${kind}` : kind;
const useElectricalWithHVAC = ({ selectedHub, sensors, }) => {
    const { branchPowerMonitorData, groupedBranchData, mainCtTotalWatts, isValidConfiguration: pmValidConfig, refresh, loading: branchLoading, error: branchError, } = (0, useBranchEnergyCalculation_1.useBranchEnergyCalculation)({ selectedHub, sensors });
    // Pass the caller's list rather than letting the hook re-fetch: it's already
    // the right scope (household, including linked gateways) and this drops a
    // duplicate per-hub sensor fetch on the Pi.
    const { hvacSensorGroups } = (0, useHVACSensorsByGroup_1.useHVACSensorsByGroup)(selectedHub?.id ?? undefined, sensors);
    const [seerDataByGroup, setSeerDataByGroup] = (0, react_1.useState)(new Map());
    const pollRef = (0, react_1.useRef)();
    const fetchAllGroups = (0, react_1.useCallback)(async () => {
        const hubId = selectedHub?.id;
        if (!hubId || hvacSensorGroups.length === 0) {
            setSeerDataByGroup(new Map());
            return;
        }
        // Each group is queried under ITS OWN hub — a gateway's condenser/air
        // handler is partitioned under the gateway, not the hub showing the list.
        const results = await Promise.all(hvacSensorGroups.map((group) => fetchGroupSeerRating(group.hubId || hubId, group.groupId)));
        const nextMap = new Map();
        hvacSensorGroups.forEach((group, idx) => {
            if (results[idx])
                nextMap.set(group.groupId, results[idx]);
        });
        setSeerDataByGroup(nextMap);
    }, [selectedHub?.id, hvacSensorGroups]);
    (0, react_1.useEffect)(() => {
        fetchAllGroups();
        pollRef.current = setInterval(fetchAllGroups, pollingConstants_1.POLLING_INTERVALS_MILLIS.HVAC);
        return () => {
            if (pollRef.current)
                clearInterval(pollRef.current);
        };
    }, [fetchAllGroups]);
    // HVAC is split-phase 240V; branch CT voltage is single-leg (~120V). Double single-leg readings
    // to approximate the true HVAC supply voltage for power math.
    const hvacVoltage = (0, react_1.useMemo)(() => {
        const singleLeg = branchPowerMonitorData?.[0]?.voltage;
        if (!singleLeg)
            return SPLIT_PHASE_VOLTAGE_FALLBACK;
        return singleLeg < SINGLE_LEG_THRESHOLD ? singleLeg * 2 : singleLeg;
    }, [branchPowerMonitorData]);
    // HVAC entries are produced whenever a sensor group exists, even when the unit isn't running.
    // A connected condenser / air handler reads 0 A when off, which is meaningful: homeowners and
    // installers should still see the equipment in the branch list so they can confirm the sensor
    // is paired.
    const hvacBranches = (0, react_1.useMemo)(() => {
        if (hvacSensorGroups.length === 0)
            return [];
        const entries = [];
        hvacSensorGroups.forEach((group, groupIndex) => {
            const seerData = seerDataByGroup.get(group.groupId);
            const rating = seerData?.lastMinuteSeerRating ?? seerData?.runningHourSeerRating;
            const timestamp = seerData?.timestamp || Date.now() / 1000;
            const label = getGroupLabel(group, groupIndex, hvacSensorGroups.length);
            const baseIndex = exports.HVAC_BRANCH_INDEX_BASE + groupIndex * 2;
            if (group.outdoorSensor) {
                entries.push({
                    branchIndex: baseIndex,
                    branchCT: rating?.condenserAvgAmperage ?? 0,
                    voltage: hvacVoltage,
                    percent: 0,
                    name: buildHvacEntryName(label, 'Condenser'),
                    timestamp,
                });
            }
            if (group.indoorSensor) {
                entries.push({
                    branchIndex: baseIndex + 1,
                    branchCT: rating?.airHandlerAvgAmperage ?? 0,
                    voltage: hvacVoltage,
                    percent: 0,
                    name: buildHvacEntryName(label, 'Air Handler'),
                    timestamp,
                });
            }
        });
        return entries;
    }, [seerDataByGroup, hvacSensorGroups, hvacVoltage]);
    const hvacTotalWatts = (0, react_1.useMemo)(() => hvacBranches.reduce((sum, b) => sum + b.branchCT * b.voltage, 0), [hvacBranches]);
    const hvacRunningTimePercentage = (0, react_1.useMemo)(() => {
        const pctValues = [];
        seerDataByGroup.forEach((seer) => {
            const pct = seer.runningHourSeerRating?.runningTimePercentage;
            if (typeof pct === 'number' && Number.isFinite(pct))
                pctValues.push(pct);
        });
        if (pctValues.length === 0)
            return null;
        return pctValues.reduce((a, b) => a + b, 0) / pctValues.length;
    }, [seerDataByGroup]);
    const hvacEstimatedDailyKwh = (0, react_1.useMemo)(() => {
        if (seerDataByGroup.size === 0)
            return 0;
        const WATT_TO_KW = 1000;
        const MS_PER_HOUR = 3_600_000;
        // Scale by hours elapsed since local midnight, not a flat 24h. Without this the hook reports
        // a full-day projection at 4 AM — way too high — and under-reports at 11 PM. Memo re-runs on
        // each SEER poll (~30s), so the elapsed value stays fresh.
        const now = new Date();
        const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const hoursElapsed = Math.max(0, Math.min(24, (now.getTime() - midnight.getTime()) / MS_PER_HOUR));
        let kwh = 0;
        seerDataByGroup.forEach((seer) => {
            const rating = seer.runningHourSeerRating;
            if (!rating)
                return;
            const amps = rating.combinedAvgAmperage
                ?? (rating.condenserAvgAmperage ?? 0) + (rating.airHandlerAvgAmperage ?? 0);
            const pct = rating.runningTimePercentage;
            if (!amps || typeof pct !== 'number' || !Number.isFinite(pct))
                return;
            const avgWhileRunningKw = (amps * hvacVoltage) / WATT_TO_KW;
            kwh += avgWhileRunningKw * (pct / 100) * hoursElapsed;
        });
        return kwh;
    }, [seerDataByGroup, hvacVoltage]);
    const combined = (0, react_1.useMemo)(() => {
        const pmBranches = branchPowerMonitorData ?? [];
        const merged = [...pmBranches, ...hvacBranches];
        if (merged.length === 0)
            return merged;
        // Prefer the panel mains as the denominator so percentages answer "what share of the whole
        // house is this branch?". Without mains data (or if branches somehow exceed mains), fall
        // back to the sum of what we do have so percentages still make internal sense.
        const accountedWatts = merged.reduce((sum, b) => sum + b.branchCT * b.voltage, 0);
        const denominator = mainCtTotalWatts > 0 ? Math.max(mainCtTotalWatts, accountedWatts) : accountedWatts;
        if (denominator <= 0)
            return merged;
        return merged.map((b) => ({
            ...b,
            percent: ((b.branchCT * b.voltage) / denominator) * 100,
        }));
    }, [branchPowerMonitorData, hvacBranches, mainCtTotalWatts]);
    const groupedBranches = (0, react_1.useMemo)(() => {
        const hvacGroupEntries = hvacSensorGroups
            .map((group, groupIndex) => {
            const baseIndex = exports.HVAC_BRANCH_INDEX_BASE + groupIndex * 2;
            const groupBranches = combined.filter((b) => b.branchIndex >= baseIndex && b.branchIndex < baseIndex + 2);
            if (groupBranches.length === 0)
                return null;
            return {
                sensorName: getGroupLabel(group, groupIndex, hvacSensorGroups.length),
                sensorId: `hvac-${group.groupId}`,
                branches: groupBranches,
            };
        })
            .filter((g) => g !== null);
        // Power-monitor groups carry their own branches verbatim, but their percentages need to be
        // re-synced from the recalculated `combined` list so they include HVAC in the denominator.
        const pmGroups = groupedBranchData.map((group) => ({
            ...group,
            branches: group.branches.map((b) => {
                const recalc = combined.find((c) => c.branchIndex === b.branchIndex && c.name === b.name);
                return recalc ?? b;
            }),
        }));
        return [...pmGroups, ...hvacGroupEntries];
    }, [combined, groupedBranchData, hvacSensorGroups]);
    return {
        branches: combined,
        groupedBranches,
        hvacBranches,
        hvacSensorGroups,
        mainCtTotalWatts,
        hvacTotalWatts,
        hvacRunningTimePercentage,
        hvacEstimatedDailyKwh,
        isValidConfiguration: pmValidConfig || hvacBranches.length > 0,
        loading: branchLoading,
        error: branchError,
        refresh,
    };
};
exports.useElectricalWithHVAC = useElectricalWithHVAC;
